import { wallClock } from "../../lib/format";
import type { Appointment, Message } from "../../lib/types/models";
import { CalendarIcon, ClockIcon } from "@ui/react/icons";
import { Button, StatusPill } from "@ui/react";
import { metaString } from "./metadata";

/**
 * Appointment status has no entry in lib/types/enums.ts — the shared vocabulary
 * covers jobs, quotes and bookings only — so the six values from
 * `types.AppointmentStatus` are mapped here rather than in the shared module,
 * which this feature does not own.
 */
type ScheduleStatus =
  | "scheduled"
  | "confirmed"
  | "inProgress"
  | "completed"
  | "cancelled"
  | "rescheduled";

const STATUS_HEADINGS: Record<ScheduleStatus, string> = {
  scheduled: "Schedule proposed",
  confirmed: "Appointment confirmed",
  inProgress: "Appointment started",
  completed: "Appointment completed",
  cancelled: "Appointment cancelled",
  rescheduled: "Appointment rescheduled",
};

const STATUSES: readonly ScheduleStatus[] = [
  "scheduled",
  "confirmed",
  "inProgress",
  "completed",
  "cancelled",
  "rescheduled",
];

function parseScheduleStatus(value: string | null | undefined): ScheduleStatus {
  if (!value) return "scheduled";
  const normalized = value.toLowerCase().replace(/[-_\s]/g, "");
  return STATUSES.find((status) => status.toLowerCase() === normalized) ?? "scheduled";
}

/**
 * Last resort only. The mobile card infers status by string-matching the system
 * message's copy, which the backend writes in English ("Appointment confirmed
 * for …"). It is used here solely when the appointment document itself could
 * not be loaded, so a stale card still reads roughly right instead of claiming
 * every cancelled slot is still pending.
 */
function inferStatusFromContent(content: string): ScheduleStatus {
  const text = content.toLowerCase();
  if (text.includes("cancelled")) return "cancelled";
  if (text.includes("rescheduled")) return "rescheduled";
  if (text.includes("confirmed")) return "confirmed";
  return "scheduled";
}

export function ScheduleCard({
  message,
  appointment,
  onCancel,
  onReschedule,
}: {
  message: Message;
  appointment?: Appointment | null;
  /** Omitted (or the appointment isn't loaded) hides both actions — there is
   *  nothing live to act on. The thread withholds them once the job is closed,
   *  so this is what makes a read-only thread read-only: optional-chaining a
   *  missing handler left two live-looking buttons that did nothing, which is
   *  worse than either offering them or not. */
  onCancel?: (appointment: Appointment) => void;
  onReschedule?: (appointment: Appointment) => void;
}) {
  // The system message's metadata carries the wall-clock strings verbatim; the
  // appointment document is preferred when it is loaded because only it knows
  // the CURRENT status (the message is immutable by firestore.rules).
  const scheduledDate = appointment?.scheduledDate ?? metaString(message.metadata, "scheduledDate");
  const startTime = appointment?.startTime ?? metaString(message.metadata, "startTime");
  const endTime = appointment?.endTime ?? metaString(message.metadata, "endTime");
  const notes = appointment?.notes ?? metaString(message.metadata, "notes");

  const status = appointment
    ? parseScheduleStatus(appointment.status)
    : inferStatusFromContent(message.content);

  return (
    <div className="thread-card">
      <span className="cluster cluster--between">
        <span className="cluster">
          <CalendarIcon />
          <span className="row__title">{STATUS_HEADINGS[status]}</span>
        </span>
        <StatusPill kind="appointment" status={status} />
      </span>

      {scheduledDate ? (
        <span className="cluster">
          <ClockIcon />
          {/* Wall clock, never a Date: these strings are timezone naive and
              re-parsing them would shift the slot by the viewer's offset. */}
          <span>{wallClock(scheduledDate, startTime, endTime)}</span>
        </span>
      ) : (
        <p className="muted">{message.content}</p>
      )}

      {notes ? <p className="muted">{notes}</p> : null}

      {/* Only a live, still-pending slot can be changed — a confirmed one has
          already been agreed, and anything cancelled/rescheduled/completed is
          history. */}
      {appointment && onReschedule && onCancel && (status === "scheduled" || status === "confirmed") ? (
        <span className="cluster">
          <Button variant="tertiary" onClick={() => onReschedule(appointment)}>
            Reschedule
          </Button>
          <Button variant="tertiary" aria-label="Cancel appointment" onClick={() => onCancel(appointment)}>
            Cancel
          </Button>
        </span>
      ) : null}
    </div>
  );
}
